import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tag, Gavel, ArrowLeftRight, ChevronRight, Upload, X, ImagePlus, CreditCard, Package, PackageOpen, Layers, Shirt } from 'lucide-react';
import { useSearchCards, useCreateListing } from '@/hooks/useApi';
import { CardCondition, Card, ItemCategory } from '@/types';
import { formatPrice } from '@/utils';

type ListingType = 'sale' | 'auction' | 'trade';

const CONDITIONS = ['Mint', 'Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played'];

const LISTING_TYPE_OPTIONS: { value: ListingType; label: string; description: string; icon: React.FC<{ className?: string }> }[] = [
  { value: 'sale', label: 'Buy Now', description: 'Set a fixed price for instant purchase', icon: Tag },
  { value: 'auction', label: 'Auction', description: 'Let buyers bid — highest wins', icon: Gavel },
  { value: 'trade', label: 'Trade', description: 'Swap cards with other collectors', icon: ArrowLeftRight },
];

const ITEM_CATEGORY_OPTIONS: { value: ItemCategory; label: string; description: string; icon: React.FC<{ className?: string }> }[] = [
  { value: 'single_card',  label: 'Single Card',   description: 'One individual card',          icon: CreditCard  },
  { value: 'booster_pack', label: 'Booster Pack',  description: 'Sealed pack of cards',         icon: Package     },
  { value: 'booster_box',  label: 'Booster Box',   description: 'Full sealed box',              icon: PackageOpen },
  { value: 'bundle',       label: 'Bundle / Lot',  description: 'Mixed group of items',         icon: Layers      },
  { value: 'merch',        label: 'Merch',         description: 'Plush, accessories, apparel',  icon: Shirt       },
];

export default function CreateListingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedCardId = location.state?.cardId;

  const [step, setStep] = useState(1);
  const [listingType, setListingType] = useState<ListingType>('sale');
  const [cardSearch, setCardSearch] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(preselectedCardId ?? null);
  const [condition, setCondition] = useState<CardCondition>('Near Mint');
  const [price, setPrice] = useState('');
  const [startingPrice, setStartingPrice] = useState('');
  const [reservePrice, setReservePrice] = useState('');
  const [auctionDays, setAuctionDays] = useState('7');
  const [tradeFor, setTradeFor] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [itemCategory, setItemCategory] = useState<ItemCategory>('single_card');
  const [bulkItemName, setBulkItemName] = useState('');
  const [bulkItemBrand, setBulkItemBrand] = useState('');

  const [entryMode, setEntryMode] = useState<'search' | 'manual'>('search');
  const [manualCard, setManualCard] = useState({
    name: '', set_name: '', game: 'Pokemon', rarity: '', card_type: '', card_number: '',
  });
  const [createdCardId, setCreatedCardId] = useState<string | null>(null);

  const { data: searchResults, isLoading: searching } = useSearchCards(cardSearch);
  const { mutateAsync: createListing } = useCreateListing();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchList: Card[] = Array.isArray(searchResults)
    ? searchResults
    : (searchResults as any)?.data ?? [];
  const selectedCard = searchList.find((c) => c.id === selectedCardId) ?? null;

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const remaining = 5 - imageFiles.length;
    const newFiles = files.slice(0, remaining);

    setImageFiles((prev) => [...prev, ...newFiles]);

    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (): Promise<string[]> => {
    if (!imageFiles.length) return [];

    const formData = new FormData();
    imageFiles.forEach((file) => formData.append('images', file));

    try {
      const apiVersion = import.meta.env.VITE_API_VERSION || 'v1';
      const response = await fetch(`/api/${apiVersion}/uploads/images`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });
      const data = await response.json();
      return data.data?.urls ?? data.urls ?? [];
    } catch (err) {
      console.error('Image upload failed:', err);
      return [];
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const uploadedUrls = await uploadImages();

      if (isNonCard) {
        // Non-card item — no card lookup needed
        const listing = await createListing({
          item_category: itemCategory,
          item_name: bulkItemName,
          item_brand: bulkItemBrand || undefined,
          listing_type: listingType,
          condition,
          price: listingType === 'sale' ? Number(price) : listingType === 'auction' ? Number(startingPrice) : 0.01,
          starting_price: listingType === 'auction' ? Number(startingPrice) : undefined,
          reserve_price: listingType === 'auction' && reservePrice ? Number(reservePrice) : undefined,
          auction_days: listingType === 'auction' ? Number(auctionDays) : undefined,
          trade_for: listingType === 'trade' ? tradeFor : undefined,
          notes,
          image_urls: uploadedUrls.length ? uploadedUrls : undefined,
        });
        navigate(`/listings/${listing.id}`);
        return;
      }

      let finalCardId = effectiveCardId;

      // Create card first if manually entered
      if (entryMode === 'manual' && !finalCardId) {
        const res = await fetch('/api/v1/cards', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({
            name: manualCard.name,
            set_name: manualCard.set_name || 'Unknown',
            game: manualCard.game,
            rarity: manualCard.rarity || undefined,
            card_type: manualCard.card_type || undefined,
            card_number: manualCard.card_number || undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error?.message ?? 'Failed to create card');
        finalCardId = data.data.id;
        setCreatedCardId(finalCardId);
      }

      if (!finalCardId) return;

      const listing = await createListing({
        card_id: finalCardId,
        item_category: 'single_card',
        listing_type: listingType,
        condition,
        price: listingType === 'sale' ? Number(price) : listingType === 'auction' ? Number(startingPrice) : 0.01,
        starting_price: listingType === 'auction' ? Number(startingPrice) : undefined,
        reserve_price: listingType === 'auction' && reservePrice ? Number(reservePrice) : undefined,
        auction_days: listingType === 'auction' ? Number(auctionDays) : undefined,
        trade_for: listingType === 'trade' ? tradeFor : undefined,
        notes,
        image_urls: uploadedUrls.length ? uploadedUrls : undefined,
      });

      navigate(`/listings/${listing.id}`);
    } catch (err: any) {
      console.error('Failed to create listing:', err?.response?.data ?? err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const effectiveCardId = selectedCardId ?? createdCardId;
  const isNonCard = itemCategory !== 'single_card';
  const canProceedStep1 = isNonCard
    ? !!bulkItemName.trim()
    : (!!effectiveCardId || (entryMode === 'manual' && !!manualCard.name && !!manualCard.game));
  const canProceedStep2 =
    listingType === 'sale' ? !!price :
    listingType === 'auction' ? !!startingPrice :
    !!tradeFor;

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-accent-500 text-white py-10">
        <div className="container-custom">
          <h1 className="text-3xl font-display font-bold mb-1">Create a Listing</h1>
          <p className="text-brand-100">List your card for sale, auction, or trade</p>
        </div>
      </div>

      <div className="container-custom py-8 max-w-2xl">
        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step > s ? 'bg-brand-600 text-white' :
                step === s ? 'bg-brand-600 text-white ring-4 ring-brand-100' :
                'bg-gray-200 dark:bg-zinc-700 text-gray-500 dark:text-zinc-500'
              }`}>
                {step > s ? '✓' : s}
              </div>
              {s < 3 && <ChevronRight className="h-4 w-4 text-gray-400 dark:text-zinc-500" />}
            </div>
          ))}
          <div className="ml-2 flex gap-8 text-sm font-medium">
            <span className={step >= 1 ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-500'}>{isNonCard ? 'Item Details' : 'Select Card'}</span>
            <span className={step >= 2 ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-500'}>Pricing</span>
            <span className={step >= 3 ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-500'}>Review</span>
          </div>
        </div>

        {/* Step 1: Item type + details */}
        {step === 1 && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6">

            {/* Item category selector */}
            <h2 className="text-xl font-bold mb-3">What are you listing?</h2>
            <div className="grid grid-cols-5 gap-2 mb-6">
              {ITEM_CATEGORY_OPTIONS.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setItemCategory(value)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all ${
                    itemCategory === value
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                      : 'border-gray-200 dark:border-zinc-700 hover:border-gray-300 dark:hover:border-zinc-600'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${itemCategory === value ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-400'}`} />
                  <span className={`text-xs font-medium leading-tight ${itemCategory === value ? 'text-brand-700 dark:text-brand-300' : 'text-gray-600 dark:text-zinc-400'}`}>{label}</span>
                </button>
              ))}
            </div>

            {/* Non-card item fields */}
            {isNonCard && (
              <div className="space-y-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Item Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder={itemCategory === 'merch' ? 'e.g. Pikachu Plush 12"' : itemCategory === 'booster_box' ? 'e.g. Scarlet & Violet Booster Box' : 'e.g. Temporal Forces Booster Pack'}
                    value={bulkItemName}
                    onChange={(e) => setBulkItemName(e.target.value)}
                    className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Brand / Game</label>
                  <input
                    type="text"
                    placeholder="e.g. Pokemon, Magic: The Gathering, Nintendo"
                    value={bulkItemBrand}
                    onChange={(e) => setBulkItemBrand(e.target.value)}
                    className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800"
                  />
                </div>
              </div>
            )}

            {/* Toggle search vs manual — single cards only */}
            {!isNonCard && <></> /* spacer */}
            {!isNonCard && (
            <>{/* Toggle search vs manual */}
            <div className="flex gap-2 mb-5">
              <button
                type="button"
                onClick={() => setEntryMode('search')}
                className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${entryMode === 'search' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-400 border-gray-200 dark:border-zinc-700 hover:border-gray-300 dark:hover:border-zinc-600'}`}
              >
                Search Database
              </button>
              <button
                type="button"
                onClick={() => setEntryMode('manual')}
                className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${entryMode === 'manual' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-400 border-gray-200 dark:border-zinc-700 hover:border-gray-300 dark:hover:border-zinc-600'}`}
              >
                Enter Manually
              </button>
            </div>

            {entryMode === 'search' && (
              <>
                <input
                  type="text"
                  placeholder="Search by card name..."
                  value={cardSearch}
                  onChange={(e) => setCardSearch(e.target.value)}
                  className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-brand-400"
                />

                {searching && <p className="text-sm text-gray-500 dark:text-zinc-500 mb-4">Searching...</p>}

                {searchList.length > 0 && (
                  <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
                    {searchList.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => setSelectedCardId(card.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                          selectedCardId === card.id
                            ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20'
                            : 'border-gray-200 dark:border-zinc-700 hover:border-gray-300 dark:hover:border-zinc-600'
                        }`}
                      >
                        {card.image_url ? (
                          <img src={card.image_url} alt={card.name} className="h-12 w-9 object-cover rounded" />
                        ) : (
                          <div className="h-12 w-9 bg-gray-100 dark:bg-zinc-800 rounded flex items-center justify-center text-xl">🃏</div>
                        )}
                        <div>
                          <p className="font-semibold text-sm">{card.name}</p>
                          <p className="text-xs text-gray-500 dark:text-zinc-500">{card.set_name} · {card.rarity}</p>
                        </div>
                        {card.market_price && (
                          <p className="ml-auto text-brand-600 dark:text-brand-400 font-bold text-sm">{formatPrice(card.market_price)}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {!searching && searchList.length === 0 && cardSearch.length > 1 && (
                  <p className="text-sm text-gray-500 dark:text-zinc-500 mb-4">No cards found. Try entering manually.</p>
                )}
              </>
            )}

            {entryMode === 'manual' && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Charizard"
                    value={manualCard.name}
                    onChange={(e) => setManualCard(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Game <span className="text-red-500">*</span></label>
                  <select
                    value={manualCard.game}
                    onChange={(e) => setManualCard(prev => ({ ...prev, game: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  >
                    <option>Pokemon</option>
                    <option>Magic</option>
                    <option>YuGiOh</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Set Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Base Set"
                      value={manualCard.set_name}
                      onChange={(e) => setManualCard(prev => ({ ...prev, set_name: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Number</label>
                    <input
                      type="text"
                      placeholder="e.g. 4/102"
                      value={manualCard.card_number}
                      onChange={(e) => setManualCard(prev => ({ ...prev, card_number: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Rarity</label>
                    <input
                      type="text"
                      placeholder="e.g. Holo Rare"
                      value={manualCard.rarity}
                      onChange={(e) => setManualCard(prev => ({ ...prev, rarity: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Type</label>
                    <input
                      type="text"
                      placeholder="e.g. Fire"
                      value={manualCard.card_type}
                      onChange={(e) => setManualCard(prev => ({ ...prev, card_type: e.target.value }))}
                      className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-2 mb-5">
              <p className="text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Card Photos <span className="text-gray-500 dark:text-zinc-500 font-normal">(up to 5)</span></p>
              <p className="text-xs text-gray-400 dark:text-zinc-500 mb-3">First photo is the cover. Add front, back, and side shots for better buyer confidence.</p>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleImageSelect}
              />

              <div className="flex flex-wrap gap-3">
                {imagePreviews.map((src, i) => (
                  <div key={i} className="relative h-20 w-16 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-800 group">
                    <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i)}
                      className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {imagePreviews.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-20 w-16 rounded-lg border-2 border-dashed border-gray-200 dark:border-zinc-700 flex items-center justify-center hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                  >
                    <ImagePlus className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
                  </button>
                )}
              </div>

              {imagePreviews.length === 0 && (
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-200 dark:border-zinc-800 rounded-xl cursor-pointer hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors mt-3">
                  <Upload className="h-6 w-6 text-gray-500 dark:text-zinc-500 mb-1" />
                  <span className="text-sm text-gray-500 dark:text-zinc-500">Click to upload photos</span>
                  <span className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">JPG, PNG, WebP · up to 5MB each</span>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleImageSelect}
                  />
                </label>
              )}
            </div>

            {!selectedCardId && !effectiveCardId && (
              <p className="text-sm text-amber-600 text-center mb-2">⚠ Please search and select a card to continue</p>
            )}
            </>)}

            {/* Condition applies to all item types */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Condition</label>
              <div className="flex flex-wrap gap-2">
                {CONDITIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCondition(c as CardCondition)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      condition === c
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-700'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {!canProceedStep1 && isNonCard && (
              <p className="text-sm text-amber-600 text-center mb-2">⚠ Please enter an item name to continue</p>
            )}
            <button
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Listing Type & Pricing */}
        {step === 2 && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-5">How do you want to sell?</h2>

            {/* Listing Type Selection */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {LISTING_TYPE_OPTIONS.map(({ value, label, description, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setListingType(value)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    listingType === value
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                      : 'border-gray-200 dark:border-zinc-700 hover:border-gray-300 dark:hover:border-zinc-600'
                  }`}
                >
                  <Icon className={`h-6 w-6 mb-2 ${listingType === value ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-zinc-500'}`} />
                  <p className={`font-bold text-sm ${listingType === value ? 'text-brand-700 dark:text-brand-300' : 'text-gray-700 dark:text-zinc-300'}`}>{label}</p>
                  <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">{description}</p>
                </button>
              ))}
            </div>

            {/* Pricing fields per type */}
            {listingType === 'sale' && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Price (USD)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 font-medium">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-zinc-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
              </div>
            )}

            {listingType === 'auction' && (
              <div className="space-y-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Starting Bid (USD)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500">$</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={startingPrice}
                      onChange={(e) => setStartingPrice(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-zinc-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Reserve Price (optional)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500">$</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={reservePrice}
                      onChange={(e) => setReservePrice(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-zinc-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Duration</label>
                  <div className="flex gap-2">
                    {['1', '3', '7', '14'].map((d) => (
                      <button
                        key={d}
                        onClick={() => setAuctionDays(d)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          auctionDays === d ? 'bg-brand-600 text-white' : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-700'
                        }`}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {listingType === 'trade' && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">What are you looking for?</label>
                <textarea
                  placeholder="e.g. Looking for Charizard Base Set or similar rare cards..."
                  value={tradeFor}
                  onChange={(e) => setTradeFor(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                />
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">Additional Notes (optional)</label>
              <textarea
                placeholder="Any extra details about card condition, shipping, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="btn-outline flex-1">Back</button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review Listing
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6">
            <h2 className="text-xl font-bold mb-5">Review Your Listing</h2>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 mb-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-zinc-500">{isNonCard ? 'Item' : 'Card'}</span>
                <span className="font-medium">
                  {isNonCard
                    ? `${bulkItemBrand ? bulkItemBrand + ' — ' : ''}${bulkItemName}`
                    : (selectedCard?.name ?? manualCard.name ?? `Card #${selectedCardId}`)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-zinc-500">Type</span>
                <span className="font-medium capitalize">{ITEM_CATEGORY_OPTIONS.find(o => o.value === itemCategory)?.label ?? itemCategory}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-zinc-500">Condition</span>
                <span className="font-medium">{condition}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-zinc-500">Listing Type</span>
                <span className="font-medium capitalize">{listingType === 'sale' ? 'Buy Now' : listingType}</span>
              </div>
              {listingType === 'sale' && price && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-zinc-500">Price</span>
                  <span className="font-bold text-brand-600 dark:text-brand-400">{formatPrice(Number(price))}</span>
                </div>
              )}
              {listingType === 'auction' && startingPrice && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-zinc-500">Starting Bid</span>
                  <span className="font-bold text-orange-600">{formatPrice(Number(startingPrice))}</span>
                </div>
              )}
              {listingType === 'auction' && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-zinc-500">Duration</span>
                  <span className="font-medium">{auctionDays} days</span>
                </div>
              )}
              {listingType === 'trade' && tradeFor && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-zinc-500">Looking for</span>
                  <span className="font-medium text-right max-w-xs">{tradeFor}</span>
                </div>
              )}
              {notes && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-zinc-500">Notes</span>
                  <span className="font-medium text-right max-w-xs">{notes}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="btn-outline flex-1">Edit</button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {isSubmitting ? 'Publishing...' : 'Publish Listing 🎉'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}